<script setup lang="ts">
import type { Album } from "@/models"
import { useUrlSearchParams } from "@vueuse/core"
import { computed, onMounted, ref } from "vue"
import { useGetEventVault, useListAlbums } from "@/api"
import { AlbumSelector, AlbumsLayout, Footer, FreemiumBadge, MainGalleryHiddenMessage, Uploader } from "@/components"
import { emitSelectedAlbum } from "@/components/Nav/AlbumIdEventBus"
import { Alert, Button, ButtonLink, Card, Drawer, Hint, Icon } from "@/components/ui"
import { useConvertedRouteParam, useModal, useTranslation } from "@/composables"
import { GuestLayout } from "@/layouts"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { useTemplateStore } from "@/stores/templateStore"
import { UploadDrawerKey } from "@/symbols"

definePage({
  meta: {
    requiresCookies: true,
    pageLayout: "multi",
  },
})

const { t } = useTranslation()

const templateStore = useTemplateStore()
const eventVaultStore = useEventVaultStore()

const eventVaultId = useConvertedRouteParam("id", "/guest/[id]/guest-book", value => Number(value))

const urlParams = useUrlSearchParams<{ album?: number }>()

const { toggle } = useModal(UploadDrawerKey)

const albumId = ref<number | undefined>()
const infoMessage = ref<string>(t("guestbook_page.info_message"))
const noteUrl = ref<string>(
  urlParams.album
    ? `/guest/${eventVaultId.value}/message?album=${urlParams.album}`
    : `/guest/${eventVaultId.value}/message`,
)

onMounted(() => {
  if (urlParams.album) {
    albumId.value = urlParams.album
    emitSelectedAlbum(urlParams.album)
  }
})

const { data: eventVault, isLoading: isLoadingEvent } = useGetEventVault(eventVaultStore.eventVaultId)
const { data: albums, isLoading: isLoadingAlbums } = useListAlbums(eventVaultStore.eventVaultId)

function onAlbumSelect(album: Album) {
  if (album.id)
    urlParams.album = album.id
  albumId.value = album.id
}

const hideGuestbook = computed(() => {
  return !eventVault.value?.eventVaultSettings?.shareGuestbookWithGuests
})

const isGalleryUnavailable = computed(
  () =>
    eventVault.value?.eventVaultSettings?.hideMainGallery && !albumId.value && !isLoadingEvent.value && !eventVaultStore.isHost,
)

const isFreemiumEvent = computed(() => eventVault.value?.freemiumMode)
</script>

<template>
  <GuestLayout hide-footer>
    <AlbumsLayout>
      <template #sidebar>
        <AlbumSelector
          v-if="eventVaultId && eventVault?.eventVaultSettings?.albumsEnabled"
          v-model="albumId"
          :event-vault-id="eventVaultId"
          :event-cover-image="eventVault?.coverImageUrl"
          :albums="albums"
          :hide-main-gallery="eventVault.eventVaultSettings?.hideMainGallery && !eventVaultStore.isHost"
          :is-loading="isLoadingAlbums"
          @select="onAlbumSelect"
        />
      </template>
      <MainGalleryHiddenMessage
        v-if="isGalleryUnavailable"
        :page-message="t('main_gallery_hidden_message.guestbook_messages_message')"
      />
      <div v-else class="container gpx-guest-note">
        <h3 class="font-bold text-2xl mb-6">
          {{ t("guestbook_page.title") }}
        </h3>
        <Hint v-if="hideGuestbook" class="gpx-guestbook-info_mobile" :hint="infoMessage" />
        <div v-if="isLoadingEvent" class="gpx-guestbook-messages-wrapper">
          <div class="gpx-guestbook-message-type-card gpx-skeleton gpx-guestbook-message-type-card_loading" />
        </div>
        <div v-else class="gpx-guestbook-messages-wrapper">
          <Card class="gpx-guestbook-message-type-card" variant="secondary">
            <template #header>
              <h3 class="text-xl mb-3">
                {{ t("note") }}
              </h3>
            </template>
            <p class="mb-4">
              {{ t("guestbook_page.note_message") }}
            </p>
            <template #footer>
              <div class="gpx-guestbook-message-type-card-footer">
                <ButtonLink :to="noteUrl" size="lg">
                  {{ t("guestbook_page.note_button") }}
                </ButtonLink>
                <Icon size="xl" icon="Edit" />
              </div>
            </template>
          </Card>
          <Card
            v-if="eventVault?.eventVaultSettings?.allowVideoGuestbook" class="gpx-guestbook-message-type-card"
            variant="secondary"
          >
            <template #header>
              <h3 class="text-xl mb-3">
                {{ t("video") }}
              </h3>
            </template>
            <p class="mb-4">
              {{ t("guestbook_page.video_message") }}
            </p>
            <template #footer>
              <div class="gpx-guestbook-message-type-card-footer">
                <Button class="video-guestbook" size="lg" :disabled="isFreemiumEvent" @click="toggle">
                  <FreemiumBadge v-if="isFreemiumEvent" variant="icon" />
                  {{ t("guestbook_page.record_button") }}
                </Button>

                <Icon size="xl" icon="Video" />
              </div>
            </template>
          </Card>
        </div>
        <Alert
          v-if="hideGuestbook" v-model="infoMessage" variant="info" class="gpx-guestbook-info_desktop" prefix-icon="CircleInfo"
          :closable="false"
        />
      </div>
      <Footer
        :custom-logo="templateStore.customFooterImage"
        :custom-footer-links="templateStore.customFooterLinks"
        :is-freemium-event="eventVault?.freemiumMode"
      />
    </AlbumsLayout>

    <Drawer :name="UploadDrawerKey" :title="t('upload')">
      <Uploader allow-camera auto-proceed can-upload-videos :additional-file-meta="{ eventVidType: 2 }" :direct-uploads="eventVault?.directUpload" />
    </Drawer>
  </GuestLayout>
</template>

<style scoped>
.gpx-guest-note {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  color: var(--template-primary-colour);
  padding-top: 2rem;
  padding-bottom: 3rem;
}

.gpx-guestbook-messages-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.gpx-guestbook-message-type-card {
  flex: 1;
  border: 1px solid var(--template-primary-colour);
  flex-wrap: wrap;
}

.gpx-guestbook-message-type-card-footer {
  display: flex;
  justify-content: space-between;
  padding: 1rem;
}

.gpx-guestbook-info_desktop {
  display: none;
}

.gpx-guestbook-message-type-card_loading {
  height: 15rem;
}

@media (width >=768px) {
  .gpx-guest-note {
    align-items: center;
  }

  .gpx-guestbook-messages-wrapper {
    flex-direction: row;
  }

  .gpx-guestbook-message-type-card-footer {
    padding: 1rem 2rem;
  }

  .gpx-guestbook-info_desktop {
    display: flex;
  }

  .gpx-guestbook-info_mobile {
    display: none;
  }
}
</style>
